import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import CytestLink from '../../components/CytestLink'
import toast from 'react-hot-toast'
import { config } from '../../config/env'

interface ForgotPasswordForm {
  email: string
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [resetToken, setResetToken] = useState<string | null>(null)
  
  //console.log('ForgotPasswordPage component rendered')

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>()

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setIsLoading(true)
      
      const response = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: data.email
        })
      })

      const result = await response.json()

      if (response.ok) {
        setEmailSent(true)
        if (config.DEBUG && result.reset_token) {
          setResetToken(result.reset_token)
        } else {
          setResetToken(null)
        }
        toast.success('Password reset link generated successfully!')
      } else {
        // Show detailed error message
        const errorMsg = result.detail || 'Failed to send reset email'
        toast.error(errorMsg)
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      toast.error('An error occurred while processing your request')
    } finally {
      setIsLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-extrabold text-gray-900">
              Password Reset Link Generated
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Your password reset link is ready
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Success!</h3>
              
              {config.DEBUG && resetToken ? (
                <div className="mt-4">
                  <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-3">
                      🔧 Development Mode
                    </p>
                    <p className="text-xs text-blue-700 mb-3">
                      In production, a reset link would be sent to your email. 
                      For testing, click the button below:
                    </p>
                    <Link
                      to={`/reset-password?token=${resetToken}`}
                      className="inline-flex items-center justify-center w-full px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Reset Your Password Now →
                    </Link>
                    <p className="mt-2 text-xs text-gray-500">
                      Token: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{resetToken}</code>
                    </p>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-gray-600">
                  Please check your email for the password reset link.
                </p>
              )}
              
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              type="email"
              autoComplete="email"
              className="mt-1 input"
              placeholder="Enter your email address"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary flex justify-center py-3"
            >
              {isLoading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-gray-600">
              Remember your password?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
